import { useState } from 'react';
import { api, ApiError } from '../api.ts';
import { useSession } from '../session.tsx';
import { Card, Checkbox, ErrorState, Field, Loading, Modal, Tag, useAsync, useToast } from '../ui.tsx';
import { EVENT_LABEL, fmtDateTime } from '../format.ts';

interface Integration {
  id: string; kind: string; name: string; enabled: 0 | 1;
  public_config: Record<string, string>; has_secret: boolean; events: string[];
  created_at: string; updated_at: string;
}

const KIND_LABEL: Record<string, string> = {
  meta_pixel: 'Meta Pixel (navegador)',
  meta_capi: 'Meta Conversions API (servidor)',
  google_analytics: 'Google Analytics 4',
  webhook: 'Webhook genérico',
  crm: 'CRM',
  checkout: 'Plataforma de checkout',
  form: 'Serviço de formulários',
};

const KIND_HELP: Record<string, string> = {
  meta_pixel: 'O ID do Pixel é público e vai para o player. Os eventos do Orbs são enviados com o mesmo event_id da CAPI, então a Meta desduplica.',
  meta_capi: 'O access token fica cifrado no backend e nunca chega ao navegador. Enviamos ViewContent, Lead, InitiateCheckout e Purchase.',
  google_analytics: 'Usa o Measurement Protocol. O API secret fica só no servidor.',
  webhook: 'Recebe um POST JSON assinado com HMAC SHA-256 no cabeçalho X-Orbs-Signature.',
  crm: 'Mesmo formato do webhook — aponte para o endpoint de captura do seu CRM.',
  checkout: 'Use para receber checkout_clicked e devolver purchase com o session_id do Orbs.',
  form: 'Encaminha os leads capturados dentro do player.',
};

export function IntegrationsPage(): JSX.Element {
  const { workspace, canWrite } = useSession();
  const toast = useToast();
  const [editing, setEditing] = useState<Integration | 'new' | null>(null);

  const data = useAsync(
    () => api<{ integrations: Integration[]; catalog: { kind: string; fields: string[]; secretLabel: string | null }[]; events: string[] }>('/api/integrations'),
    [workspace?.id],
  );
  const deliveries = useAsync(() => api<{ deliveries: any[] }>('/api/integrations/deliveries'), [workspace?.id]);

  async function remove(id: string): Promise<void> {
    try {
      await api(`/api/integrations/${id}`, { method: 'DELETE' });
      toast('Integração removida.', 'ok');
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao remover.', 'error');
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Integrações</h1>
          <p className="page-sub">Pixel, Conversions API, GA4, webhooks, CRMs e checkout. Tokens privados ficam no backend.</p>
        </div>
        <button type="button" className="btn" disabled={!canWrite} onClick={() => setEditing('new')}>Nova integração</button>
      </div>

      {data.loading && <Loading />}
      {data.error && <ErrorState message={data.error} onRetry={data.reload} />}

      <div className="grid cols-2">
        {(data.data?.integrations ?? []).map((integration) => (
          <Card
            key={integration.id}
            title={integration.name}
            subtitle={KIND_LABEL[integration.kind] ?? integration.kind}
            actions={
              <div className="row tight">
                <button type="button" className="btn ghost small" disabled={!canWrite} onClick={() => setEditing(integration)}>Editar</button>
                <button type="button" className="btn ghost small" disabled={!canWrite} onClick={() => remove(integration.id)}>Remover</button>
              </div>
            }
          >
            <div className="row tight" style={{ marginBottom: 10 }}>
              <Tag tone={integration.enabled ? 'ok' : 'muted'}>{integration.enabled ? 'Ativa' : 'Desativada'}</Tag>
              {integration.has_secret && <Tag tone="info">Segredo configurado</Tag>}
              {integration.events.length > 0 && <Tag tone="muted">{integration.events.length} evento(s)</Tag>}
            </div>
            {Object.entries(integration.public_config).map(([key, value]) => (
              <p key={key} className="card-sub" style={{ margin: 0, wordBreak: 'break-all' }}>
                <b>{key}</b>: {value}
              </p>
            ))}
            <p className="card-sub" style={{ marginTop: 8 }}>Atualizada em {fmtDateTime(integration.updated_at)}</p>
          </Card>
        ))}
      </div>

      {data.data && data.data.integrations.length === 0 && !data.loading && (
        <Card title="Nenhuma integração ainda">
          <p style={{ color: 'var(--text-3)', margin: 0 }}>
            Conecte o Meta Pixel para medir no navegador e a Conversions API para medir no servidor — os dois usam o
            mesmo <code>event_id</code>, então não há contagem dupla.
          </p>
        </Card>
      )}

      <div style={{ height: 18 }} />

      <Card title="Entregas recentes" subtitle="Cada evento é entregue no máximo uma vez por integração.">
        {deliveries.loading && <Loading />}
        {deliveries.data && deliveries.data.deliveries.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Quando</th><th>Integração</th><th>Evento</th><th>Status</th></tr></thead>
              <tbody>
                {deliveries.data.deliveries.slice(0, 40).map((delivery) => (
                  <tr key={delivery.id}>
                    <td>{fmtDateTime(delivery.created_at)}</td>
                    <td>{delivery.integration_name}</td>
                    <td><code style={{ fontSize: 11 }}>{String(delivery.event_id).slice(0, 20)}</code></td>
                    <td><Tag tone={delivery.status === 'sent' ? 'ok' : 'danger'}>{delivery.status}</Tag></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: 'var(--text-3)', margin: 0 }}>Nenhuma entrega registrada ainda.</p>
        )}
      </Card>

      {editing && (
        <IntegrationForm
          integration={editing === 'new' ? null : editing}
          events={data.data?.events ?? []}
          catalog={data.data?.catalog ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); data.reload(); }}
        />
      )}
    </div>
  );
}

function IntegrationForm({
  integration, events, catalog, onClose, onSaved,
}: {
  integration: Integration | null;
  events: string[];
  catalog: { kind: string; fields: string[]; secretLabel: string | null }[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [kind, setKind] = useState(integration?.kind ?? 'meta_pixel');
  const [name, setName] = useState(integration?.name ?? '');
  const [enabled, setEnabled] = useState(integration ? integration.enabled === 1 : true);
  const [config, setConfig] = useState<Record<string, string>>(integration?.public_config ?? {});
  const [secret, setSecret] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>(integration?.events ?? []);
  const [busy, setBusy] = useState(false);

  const definition = catalog.find((entry) => entry.kind === kind);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      await api('/api/integrations', {
        body: {
          ...(integration ? { id: integration.id } : {}),
          kind, name, enabled,
          public_config: config,
          ...(secret ? { secret } : {}),
          events: selectedEvents,
        },
      });
      toast('Integração salva.', 'ok');
      onSaved();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao salvar.', 'error');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={integration ? 'Editar integração' : 'Nova integração'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn" disabled={busy || !name} onClick={save}>
            {busy ? <span className="spinner-inline" /> : null} Salvar
          </button>
        </>
      }
    >
      <Field label="Tipo" id="i-kind">
        <select id="i-kind" value={kind} onChange={(e) => { setKind(e.target.value); setConfig({}); }} disabled={Boolean(integration)}>
          {Object.entries(KIND_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </Field>
      <p className="card-sub" style={{ marginTop: -6, marginBottom: 14 }}>{KIND_HELP[kind]}</p>

      <Field label="Nome" id="i-name">
        <input id="i-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Pixel principal" />
      </Field>

      {(definition?.fields ?? []).map((fieldName) => (
        <Field key={fieldName} label={fieldName} id={`i-${fieldName}`}>
          <input
            id={`i-${fieldName}`}
            value={config[fieldName] ?? ''}
            onChange={(e) => setConfig({ ...config, [fieldName]: e.target.value })}
            placeholder={fieldName === 'url' ? 'https://…' : ''}
          />
        </Field>
      ))}

      {definition?.secretLabel && (
        <Field
          label={definition.secretLabel}
          id="i-secret"
          hint={integration?.has_secret ? 'Já existe um segredo salvo. Preencha apenas se quiser substituí-lo.' : 'Fica cifrado no banco e nunca é devolvido pela API.'}
        >
          <input id="i-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" />
        </Field>
      )}

      <Checkbox checked={enabled} onChange={setEnabled} label="Integração ativa" />

      <h3 className="card-title" style={{ marginTop: 16 }}>Eventos enviados</h3>
      <p className="card-sub" style={{ marginTop: 0 }}>Sem seleção, todos os eventos elegíveis são enviados.</p>
      <div className="grid cols-3">
        {events.map((event) => (
          <label key={event} className="checkline">
            <input
              type="checkbox"
              checked={selectedEvents.includes(event)}
              onChange={(e) =>
                setSelectedEvents(e.target.checked ? [...selectedEvents, event] : selectedEvents.filter((name) => name !== event))
              }
            />
            {EVENT_LABEL[event] ?? event}
          </label>
        ))}
      </div>
    </Modal>
  );
}
